import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import { getFinalFilename } from '../../../../utils/filenameUtils';
import { BetterSuspense } from '../BetterSuspense';
import { useExportModal } from '../sidebar/hooks';
import { ImageExportTypes } from '../sidebar/types';
import { useExportCanvas } from './hooks';
import { exportAsImage, exportAsPdf } from './utils';

export const GraphImage = styled(`img`)`
  width: 100%;
  object-fit: contain;
`;

export const StyledDialog = styled(Dialog)`
  && {
    padding: 19px;
  }
`;

export const StyledDialogTitle = styled(DialogTitle)`
  && {
    height: 57px;
    background: rgb(38, 166, 154);
  }
`;

export const StyledDialogActions = styled(DialogActions)`
  && {
    justify-content: flex-start;
    padding: 24px;
  }
`;

export const StyledDialogContent = styled(DialogContent)`
  && {
    padding: 24px;
  }
`;

export const StyledFormControl = styled(FormControl)`
  && {
    width: 100px;
    margin: 0 10px;
  }
`;

export const StyledButton = styled(Button)`
  && {
    color: rgb(38, 166, 154);
  }
`;

interface ExportDialogInnerProps {
  exportedElementId: string;
  cacheKey: string;
  exportType: ImageExportTypes;
  title: string;
}

const ExportDialogContent = (props: ExportDialogInnerProps) => {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState('');
  const { exportedElementId, cacheKey, exportType, title } = props;
  const { data: image } = useExportCanvas(exportedElementId, cacheKey);
  const exportFn = () => {
    const requiredExtension = match(exportType)
      .with(ImageExportTypes.PNG, () => '.png')
      .with(ImageExportTypes.PDF, () => '.pdf')
      .exhaustive();

    const finalFilename = getFinalFilename(fileName, requiredExtension);

    void (exportType === ImageExportTypes.PNG
      ? exportAsImage(image, finalFilename)
      : exportAsPdf(image, finalFilename));
  };

  return (
    <>
      <StyledDialogContent>
        <GraphImage src={image} />
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledFormControl>
          <TextField
            placeholder={t('common:shareExport.fileNamePlaceholder')}
            onChange={(e) => setFileName(e.target.value)}
            name="fileName"
          />
        </StyledFormControl>
        <StyledButton
          onClick={exportFn}
          disabled={!fileName.trim() || !exportType}
        >
          {t(title)}
        </StyledButton>
      </StyledDialogActions>
    </>
  );
};

interface ExportDialogProps {
  isFullScreen: boolean;
  exportedElementId: string;
  cacheKey: string;
}

const ExportDialog = (props: ExportDialogProps) => {
  const { t } = useTranslation();
  const { isFullScreen, exportedElementId, cacheKey } = props;
  const { isExportModalOpen, handleClose, exportType } = useExportModal();
  const title =
    exportType === 'png'
      ? 'common:shareExport.exportPngButton'
      : 'common:shareExport.exportPdfButton';
  return (
    <StyledDialog
      open={isExportModalOpen}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="lg"
      PaperProps={{ square: true }}
      disablePortal={isFullScreen}
    >
      <StyledDialogTitle>
        <Typography
          sx={{
            color: 'secondary.main',
          }}
        >
          {t(title)}
        </Typography>
      </StyledDialogTitle>
      <BetterSuspense wait={0} freez={600}>
        <ExportDialogContent
          title={title}
          exportType={exportType}
          exportedElementId={exportedElementId}
          cacheKey={cacheKey}
        />
      </BetterSuspense>
    </StyledDialog>
  );
};

export default ExportDialog;
